import { useRef, useState } from 'react';
import { ArrowUp, ChevronDown, CornerUpRight, ListPlus, Paperclip, Square } from 'lucide-react';
import { toast } from 'sonner';
import type { RunMessageOutcome } from '@shared/types/factory';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { isSendKey } from '@/lib/send-key';
import { Textarea } from '@/components/ui/textarea';
import { PendingFiles, type PendingFile } from '@/components/tasks/PendingFiles';
import { pendingKey } from '@/components/tasks/clipboard-files';
import { uploadFile, withinSizeLimit } from '@/components/tasks/task-files';
import { deleteTaskAttachment } from '@/lib/tasks-api';
import { describeApiError } from '@/lib/api-errors';
import { sendRunMessage } from '@/lib/runs-api';
import type { ApiResponse } from '@/lib/api-contract';
import { messageOutcomeText } from './live-ui';

/** Agent tab messages are at most this long (the hub's AGENT_MESSAGE_MAX_CHARS). */
const MESSAGE_MAX_CHARS = 20_000;

export interface AgentMessageBoxProps {
  taskId: string;
  runId: string;
  runNumber: number;
  project?: string;
  /** A turn is running now: Steer can go into it. */
  canSteer: boolean;
  /** Queue can go: the run is live and going (its next turn), or it has finished (a Follow up). Null: why not. */
  queueBlocked: string | null;
  /** SF-T-209: why nothing can be sent now (the machine is offline, the thread is gone), or null. */
  readOnly?: string | null;
  /** After a message went: the run, or a new Follow up, changed. */
  onSent?: (outcome: RunMessageOutcome) => void;
  /** Preview only (the /dev/ui gallery): answers instead of the hub. */
  send?: typeof sendRunMessage;
  /** SF-T-266: Stop, while the agent works and the box is empty (a live run's turn); resolves once it's asked. */
  onStop?: (() => Promise<void>) | null;
}

/**
 * SF-T-209: the Agent tab's box: write to the run's agent and **Queue** it (after the turn; a parked live run starts
 * its next turn straight away, a finished run's thread carries on as a Follow up) or **Steer** it (into the turn
 * that's running). SF-T-266: one button, as t3code's: Send while the agent is idle, Queue while it works with Steer in
 * its menu, Steer alone when the run can't hold a message. Pictures go with Queue. Enter sends, Shift+Enter is a new line (SF-T-266). Nothing goes on
 * the task's timeline.
 */
export function AgentMessageBox({ taskId, runId, runNumber, project, canSteer: steerable, queueBlocked: blocked, readOnly = null, onSent, send = sendRunMessage, onStop = null }: AgentMessageBoxProps) {
  const [stopping, setStopping] = useState(false);
  const canSteer = steerable && !readOnly;
  const queueBlocked = readOnly ?? blocked;
  const [text, setText] = useState('');
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [sending, setSending] = useState<'steer' | 'queue' | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const body = text.trim();
  const tooLong = body.length > MESSAGE_MAX_CHARS ? 'At most 20,000 characters' : null;
  const uploading = pending.some((p) => p.uploading);

  const stage = (files: File[]) => {
    for (const file of withinSizeLimit(files).filter((f) => f.type.startsWith('image/'))) {
      const key = pendingKey(file);
      const upload = uploadFile(taskId, file).then((attachmentId) => {
        setPending((p) => (attachmentId ? p.map((x) => (x.key === key ? { ...x, attachmentId, uploading: false } : x)) : p.filter((x) => x.key !== key)));
        return attachmentId;
      });
      setPending((p) => [...p, { key, file, attachmentId: null, uploading: true, upload }]);
    }
  };
  const remove = (item: PendingFile) => {
    setPending((p) => p.filter((x) => x.key !== item.key));
    void item.upload?.then((attachmentId) => {
      if (attachmentId) void deleteTaskAttachment(taskId, attachmentId);
    });
  };

  const go = async (mode: 'steer' | 'queue') => {
    if (tooLong || sending || uploading) return;
    // SF-T-266: pictures can be queued on their own; Steer needs words.
    if (!body && (mode === 'steer' || !pending.length)) return;
    if (mode === 'steer' && !canSteer) return;
    if (mode === 'queue' && queueBlocked) return;
    setSending(mode);
    const ids = mode === 'queue' ? pending.map((p) => p.attachmentId).filter((id): id is string => Boolean(id)) : [];
    const res: ApiResponse<RunMessageOutcome> = await send(runId, { mode, body, ...(ids.length ? { attachment_ids: ids } : {}) }, { project });
    setSending(null);
    if (!res.data) {
      toast.error(describeApiError(res, `Couldn't send it to R-${runNumber}'s agent`));
      return;
    }
    toast.success(messageOutcomeText(res.data));
    setText('');
    if (mode === 'queue') setPending([]);
    onSent?.(res.data);
  };

  // SF-T-266: what the one button does: Queue (Send when the agent is idle: its next turn starts now), or Steer when
  // the run can't hold a message but the agent is working.
  const primary: 'steer' | 'queue' = queueBlocked && canSteer ? 'steer' : 'queue';
  const canSteerInstead = primary === 'queue' && canSteer && !queueBlocked;
  // SF-T-266: as T3 Code's: with nothing written while the agent works, the button stops it.
  const showStop = Boolean(onStop) && canSteer && !body && !pending.length && !readOnly;
  const canGo =
    primary === 'steer'
      ? Boolean(body) && !tooLong && pending.length === 0
      : (Boolean(body) || pending.length > 0) && !tooLong && !queueBlocked && !uploading;
  const PrimaryIcon = primary === 'steer' ? CornerUpRight : canSteer ? ListPlus : ArrowUp;
  const primaryLabel = primary === 'steer' ? 'Steer' : canSteer ? 'Queue' : 'Send';
  const primaryHint =
    primary === 'steer'
      ? pending.length
        ? "Pictures only go with Queue, and this run can't hold a message: send them once it ends"
        : 'Into the turn the agent is working on now, without stopping it (Enter)'
      : (queueBlocked ?? (canSteer ? 'After the turn that’s running: you can change or remove it until then (Enter)' : 'The agent starts on it now (Enter)'));
  // SF-T-266: what the button will do, in words beside it, so Send (a new turn now) is never taken for Queue.
  const modeNote = readOnly
    ? null
    : primary === 'steer'
      ? 'Working: Steer goes in now'
      : queueBlocked
        ? null
        : canSteer
          ? 'Working: Queue goes after'
          : 'Idle: Send starts a turn';

  return (
    <div className="space-y-2 rounded-lg border bg-card p-2 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/20">
      {readOnly ? <p className="px-1 text-xs text-muted-foreground">{readOnly}</p> : null}
      <Textarea
        aria-label={`Message for R-${runNumber}'s agent`}
        disabled={Boolean(readOnly)}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder={`Write to R-${runNumber}'s agent…`}
        className="min-h-14 border-0 p-1 shadow-none focus-visible:ring-0"
        onKeyDown={(e) => {
          if (isSendKey(e)) {
            e.preventDefault();
            void go(primary);
          }
        }}
        onPaste={(e) => {
          const files = [...e.clipboardData.files].filter((f) => f.type.startsWith('image/'));
          if (!files.length) return;
          e.preventDefault();
          stage(files);
        }}
      />
      <PendingFiles items={pending} onRemove={remove} />
      {tooLong ? <p className="px-1 text-xs text-destructive">{tooLong}</p> : null}
      <div className="flex flex-wrap items-center gap-1">
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = '';
            if (files.length) stage(files);
          }}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={() => fileInput.current?.click()}
          disabled={Boolean(queueBlocked)}
          aria-label="Attach pictures"
          data-hint="Pictures go with Queue: the agent sees them in its next turn"
        >
          <Paperclip className="size-4" />
        </Button>
        {modeNote ? (
          <span className="min-w-0 flex-1 truncate px-1 text-right text-2xs text-muted-foreground" data-agent-box-mode>
            {modeNote}
          </span>
        ) : (
          <span className="ml-auto" />
        )}
        {/* SF-T-266: one button, as t3code's: Send when the agent is idle, Queue while it works (with Steer in its menu), Steer when only that can go. */}
        {showStop ? (
          <Button
            size="sm"
            variant="destructive"
            loading={stopping}
            onClick={() => {
              setStopping(true);
              void onStop!().finally(() => setStopping(false));
            }}
            data-hint="Stop the agent's turn: the run waits for your next message"
          >
            <Square className="size-3.5 fill-current" />
            Stop
          </Button>
        ) : (
        <div className="flex items-center">
          <Button
            size="sm"
            className={cn(canSteerInstead && 'rounded-r-none')}
            disabled={!canGo}
            loading={sending !== null}
            onClick={() => void go(primary)}
            data-hint={primaryHint}
          >
            <PrimaryIcon className="size-4" />
            {primaryLabel}
          </Button>
          {canSteerInstead ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="rounded-l-none border-l border-primary-foreground/30 px-1.5" disabled={sending !== null} aria-label="More ways to send">
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={!body || Boolean(tooLong) || pending.length > 0} onSelect={() => void go('steer')}>
                  <CornerUpRight />
                  <div>
                    <div>Steer into this turn</div>
                    <div className="text-2xs text-muted-foreground">{pending.length ? 'Pictures only go with Queue' : 'The agent reads it now, without stopping'}</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        )}
      </div>
    </div>
  );
}
